import axios from 'axios';

export const API_BASE_URL = 'https://www.socialeye.cloud/api';
//export const API_BASE_URL = 'http://localhost:5000/api';

// --- API Client with Interceptor ---
export const api = axios.create({
    baseURL: API_BASE_URL,
});



api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// --- Utility and Gemini Fetch Helpers ---

const delay = ms => new Promise(res => setTimeout(res, ms));

export function getContentText(content) {
    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            return parsed.text || content;
        } catch (e) {
            return content;
        }
    }
    if (content && typeof content === 'object' && content !== null) {
        return content.text || JSON.stringify(content);
    }
    return content || '[No Content]';
}

export function getAuthorName(author) {
    if (typeof author === 'string') {
        return author;
    }
    if (author && typeof author === 'object') {
        return author.name || 'Unknown';
    }
    return 'Unknown';
}

export function generateTopicCloud(mentions) {
    const stopWords = new Set(['a','jazzcash','#jazzcash', 'an', 'the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'is', 'it', 'i', 'you', 'he', 'she', 'they', 'we', 'my', 'your', 'his', 'her', 'their', 'our', 'with', 'from', 'by', 'as', 'this', 'that', 'these', 'those', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'shall', 'should', 'can', 'could', 'may', 'might', 'must', 'about', 'https', 't.co']);
    const wordCounts = {};
    
    mentions.forEach(mention => {
        const text = getContentText(mention.content);
        if (typeof text !== 'string') return;
        const words = text.toLowerCase().replace(/[^a-z\s#]/g, '').split(/\s+/);
        words.forEach(word => {
            if (word && !stopWords.has(word) && word.length > 2) {
                wordCounts[word] = (wordCounts[word] || 0) + 1;
            }
        });
    });

    return Object.entries(wordCounts)
    .filter(([, count]) => count > 4)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 30)
        .map(([word, count]) => ({ word, count }));
}

export async function geminiFetchSentiment(text) {
    await delay(200); 
    const contentToAnalyze = getContentText(text);
    if (!contentToAnalyze || contentToAnalyze.trim() === '') return 'Neutral';

    const GEMINI_API_KEY = "";
   const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
       try {
        const prompt = `Analyze the sentiment of the following text based on intent and tone in terms of financial services. Respond with a single word: "Positive", "Negative", or "Neutral". Text: "${contentToAnalyze}"`;
        const payload = { contents: [{ parts: [{ text: prompt }] }] };
        const response = await fetch(GEMINI_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error(`API request failed`);
        const result = await response.json();
        const sentiment = result.candidates[0].content.parts[0].text.trim().replace('.', '');
        return ['Positive', 'Negative', 'Neutral'].includes(sentiment) ? sentiment : 'Unanalyzed';
    } catch (error) {
        console.error("Sentiment analysis error:", error);
        return 'Unanalyzed';
    }
}
// --- NEW: Batch Sentiment Analysis Function ---
export async function geminiFetchSentimentBatch(mentions) {
    if (!mentions || mentions.length === 0) return [];
    
    const mentionsToAnalyze = mentions.map(m => ({
        id: m.id,
        text: getContentText(m.content).substring(0, 500) // Truncate for safety
    }));

    await delay(1000); // Add a longer delay per batch to be safe with rate limits

    const GEMINI_API_KEY = "AIzaSyAEmvdm7uw46OCEQrhlTatZqIyd5TOvDNc"; // Leave empty for automatic injection in a compatible environment
  const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
      try {
        const prompt = `Analyze the sentiment for each object in the following JSON array in context of financial services and digital bank. Return a valid JSON array of objects, where each object has the original "id" and a new "sentiment" key with one of the following string values: "Positive", "Negative", or "Neutral".\n\nInput: ${JSON.stringify(mentionsToAnalyze)}`;
        
        const payload = {
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { responseMimeType: "application/json" }
        };

        const response = await fetch(GEMINI_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error(`API request failed with status ${response.status}`);
        
        const result = await response.json();
        const analyzedText = result.candidates[0].content.parts[0].text;
        
        return JSON.parse(analyzedText);
    } catch (error) {
        console.error("Batch sentiment analysis error:", error);
        // On failure, return the original IDs with an "Unanalyzed" status
        return mentions.map(m => ({ id: m.id, sentiment: 'Unanalyzed' }));
    }
}

export async function geminiFetchNews(query) {
    await delay(200);
    const GEMINI_API_KEY = "AIzaSyAEmvdm7uw46OCEQrhlTatZqIyd5TOvDNc";
    const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
        try {
        const prompt = `
            Use Google Search to find the 10 most recent and relevant news articles about "${query}" within last week.
            For each article, provide the real title, a brief summary, and the exact source URL.
            Respond with a valid JSON array of objects, where each object has "title", "summary","publishedDate" and "url" properties.
        `;
        const payload = {
            contents: [{ parts: [{ text: prompt }] }],
            generationConfig: { responseMimeType: "application/json" }
        };
        const response = await fetch(GEMINI_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error(`API request failed`);
        const result = await response.json();
       // console.log(result);
        const newsText = result.candidates[0].content.parts[0].text;
        console.log(newsText);
        return JSON.parse(newsText);
    } catch (error) {
        console.error("News search error:", error);
        throw error;
    }
}

export async function geminiFetchGender(name) {
    await delay(200);
    if (!name || typeof name !== 'string') return 'Unknown';
    const GEMINI_API_KEY = "AIzaSyAEmvdm7uw46OCEQrhlTatZqIyd5TOvDNc";
   const GEMINI_API_URL = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash:generateContent?key=${GEMINI_API_KEY}`;
   
   try {
        const prompt = `Based on the name "${name}", is the gender likely "Male", "Female", or "Unknown"? Respond with a single word.`;
        const payload = { contents: [{ parts: [{ text: prompt }] }] };
        const response = await fetch(GEMINI_API_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        if (!response.ok) throw new Error(`API request failed`);
        const result = await response.json();
        const gender = result.candidates[0].content.parts[0].text.trim().replace('.', '');
        return ['Male', 'Female'].includes(gender) ? gender : 'Unknown';
    } catch (error) {
        console.error("Gender analysis error:", error);
        return 'Unknown';
    }
}

